import { makeAutoObservable, runInAction } from 'mobx';
import { createContext, useContext } from 'react';
import { DefaultFormFields, FormControl, FormController } from '@insurancemenu/imenu-components-lib/dist/utils/forms';
import { editUserInformation, getUserByToken } from 'api/operations/userService';
import { toastManager } from '@insurancemenu/imenu-components-lib/dist/components/Toast/toastManager';
import { ToastColorTypes } from '@insurancemenu/imenu-components-lib/dist/types';
import { getRootStoreInstance, RootStore } from 'store/rootStore';

interface AccountSettingsFields extends DefaultFormFields {
  firstName: FormControl<string>;
  lastName: FormControl<string>;
  email: FormControl<string>;
}

export default class AccountSettingsPageStore {
  private _isInitialDataLoading = false;
  private _isActionLoading = false;
  private _rootStore: RootStore = getRootStoreInstance();

  public accountSettingsForm: FormController<AccountSettingsFields>;
  public get isInitialDataLoading(): boolean {
    return this._isInitialDataLoading;
  }

  public get isActionLoading(): boolean {
    return this._isActionLoading;
  }

  public get canComplete(): boolean {
    return !this._isActionLoading && !this._isInitialDataLoading && this.accountSettingsForm.isFormValid();
  }

  public async getInitialData(): Promise<void> {
    this._isInitialDataLoading = true;
    const userData = await getUserByToken();
    runInAction(() => {
      this.accountSettingsForm.populateFields({
        firstName: userData.firstName,
        lastName: userData.lastName,
        email: userData.email,
      });
      this._isInitialDataLoading = false;
    });
  }

  public async updateUserInfo(): Promise<void> {
    this._isActionLoading = true;

    await editUserInformation({
      firstName: this.accountSettingsForm.fields.firstName.value,
      lastName: this.accountSettingsForm.fields.lastName.value,
    });
    await runInAction(async () => {
      await this._rootStore.userStore.getUserData();
      this._isActionLoading = false;
      toastManager.addToast('User information updated successfully', ToastColorTypes.SUCCESS);
    });
  }

  constructor() {
    this.accountSettingsForm = new FormController({
      firstName: new FormControl<string>('', { validations: ['validName'], trim: true }),
      lastName: new FormControl<string>('', { validations: ['validName'], trim: true }),
      email: new FormControl<string>(''),
    });

    makeAutoObservable(this, {}, { autoBind: true });
  }
}

const AccountSettingsPageStoreContext = createContext(new AccountSettingsPageStore());

export const useAccountSettingsPageStore = (): AccountSettingsPageStore => {
  return useContext(AccountSettingsPageStoreContext);
};

export { AccountSettingsPageStore, AccountSettingsPageStoreContext };
